import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Input,
  Space,
  Typography,
  Alert,
  Progress,
  Tag,
  Avatar,
  Switch,
  Divider,
} from "antd";
import {
  LockOutlined,
  UnlockOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { MdOutlineLock, MdOutlineLockOpen, MdOutlineWarning } from "react-icons/md";

const { Text, Title, Paragraph } = Typography;

const LockUserModal = ({
  visible,
  onCancel,
  onConfirm,
  staffName,
  staffPosition,
  staffId,
  staffImage,
  isCurrentlyLocked = false,
  loading = false,
}) => {
  const [inputConfirm, setInputConfirm] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [shake, setShake] = useState(false);
  
  // FIX: Convert isCurrentlyLocked to boolean properly
  // When isCurrentlyLocked = true -> user is locked -> show UNLOCK modal
  // When isCurrentlyLocked = false -> user is active -> show LOCK modal
  const [lockStatus, setLockStatus] = useState(isCurrentlyLocked === true);

  useEffect(() => {
    if (visible) {
      // When modal opens, set lockStatus based on isCurrentlyLocked prop
      const isLocked = isCurrentlyLocked === true;
      console.log("Modal opened - isCurrentlyLocked:", isCurrentlyLocked, "isLocked:", isLocked);
      setLockStatus(isLocked);
      setInputConfirm("");
      setIsConfirmed(false);
    }
  }, [visible, isCurrentlyLocked]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputConfirm(value);
    const requiredWord = lockStatus ? "UNLOCK" : "LOCK";
    setIsConfirmed(value === requiredWord);
    
    // Shake animation for wrong input
    if (value && value !== requiredWord && value.length > 3) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const handleConfirm = () => {
    if (isConfirmed) {
      // lockStatus = true means currently locked -> will become unlocked (false)
      // lockStatus = false means currently active -> will become locked (true)
      onConfirm(!lockStatus);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && isConfirmed) {
      handleConfirm();
    }
  };

  const getRequiredWord = () => {
    return lockStatus ? "UNLOCK" : "LOCK";
  };

  const getActionColor = () => {
    return lockStatus ? "#10b981" : "#ef4444";
  };

  const getActionBgColor = () => {
    return lockStatus ? "#F0FDF4" : "#FEF2F2";
  };

  const getActionBorderColor = () => {
    return lockStatus ? "#D1FAE5" : "#FEE2E2";
  };

  return (
    <Modal
      title={
        <div style={styles.modalTitle}>
          <div style={{
            ...styles.iconWrapper,
            background: lockStatus ? "#F0FDF4" : "#FEF2F2",
          }}>
            {lockStatus ? (
              <UnlockOutlined style={{ ...styles.actionIcon, color: "#10b981" }} />
            ) : (
              <LockOutlined style={{ ...styles.actionIcon, color: "#ef4444" }} />
            )}
          </div>
          <span style={styles.titleText}>
            {lockStatus ? "ដោះសោបុគ្គលិក" : "ចាក់សោបុគ្គលិក"}
          </span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={520}
      centered
      closable={!loading}
      maskClosable={!loading}
      footer={null}
      className="lock-user-modal"
      style={styles.modal}
    >
      <div style={styles.content}>
        {/* Warning Alert */}
        <Alert
          message={
            <span style={{ color: getActionColor(), fontWeight: 600 }}>
              {lockStatus ? "ការដោះសោបុគ្គលិក" : "ការចាក់សោបុគ្គលិក"}
            </span>
          }
          description={
            <span style={{ color: getActionColor() }}>
              {lockStatus 
                ? "បុគ្គលិកនឹងអាចចូលប្រើប្រាស់ប្រព័ន្ធវិញបន្ទាប់ពីដោះសោ។"
                : "បុគ្គលិកនឹងមិនអាចចូលប្រើប្រាស់ប្រព័ន្ធបានទេ បន្ទាប់ពីចាក់សោ។"
              }
            </span>
          }
          type={lockStatus ? "success" : "error"}
          showIcon
          icon={lockStatus ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
          style={{
            ...styles.alert,
            backgroundColor: getActionBgColor(),
            borderColor: getActionBorderColor(),
          }}
        />

        {/* Staff Info Card */}
        <div style={styles.staffCard}>
          <div style={styles.staffAvatar}>
            <Avatar
              size={56}
              src={staffImage || "https://via.placeholder.com/56"}
              icon={<UserOutlined />}
              style={styles.avatar}
            />
          </div>
          <div style={styles.staffInfo}>
            <Text style={styles.staffLabel}>
              {lockStatus ? "បុគ្គលិកដែលត្រូវដោះសោ" : "បុគ្គលិកដែលត្រូវចាក់សោ"}
            </Text>
            <Title level={4} style={styles.staffName}>
              {staffName || "N/A"}
            </Title>
            <Text style={styles.staffPosition}>
              {staffPosition || "N/A"}
            </Text>
            {staffId && (
              <Tag color="default" style={styles.staffId}>
                ID: {staffId}
              </Tag>
            )}
          </div>
        </div>

        {/* Current Status Badge */}
        <div style={styles.statusSection}>
          <div style={styles.statusBadge}>
            <Text style={styles.statusLabel}>ស្ថានភាពបច្ចុប្បន្ន</Text>
            <Tag
              icon={lockStatus ? <LockOutlined /> : <UnlockOutlined />}
              color={lockStatus ? "error" : "success"}
              style={styles.statusTag}
            >
              {lockStatus ? "បានចាក់សោ" : "កំពុងដំណើរការ"}
            </Tag>
          </div>
        </div>

        {/* Confirmation Section */}
        <div style={styles.confirmationSection}>
          <div style={styles.confirmationHeader}>
            <MdOutlineWarning style={styles.warningIcon} />
            <Text strong style={styles.confirmationText}>
              {lockStatus 
                ? `ដើម្បីបញ្ជាក់ការដោះសោ សូមបញ្ចូលពាក្យ "${getRequiredWord()}"`
                : `ដើម្បីបញ្ជាក់ការចាក់សោ សូមបញ្ចូលពាក្យ "${getRequiredWord()}"`
              }
            </Text>
          </div>

          <Input
            value={inputConfirm}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={`Type ${getRequiredWord()} to confirm`}
            disabled={loading}
            autoFocus
            style={{
              ...styles.confirmInput,
              ...(isConfirmed ? styles.inputSuccess : {}),
              ...(shake ? styles.inputShake : {}),
            }}
            prefix={
              isConfirmed ? (
                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />
              ) : (
                <WarningOutlined style={{ color: "#faad14", fontSize: 18 }} />
              )
            }
          />

          {/* Validation Feedback */}
          {inputConfirm && !isConfirmed && (
            <div style={styles.errorFeedback}>
              <CloseOutlined style={{ color: "#ff4d4f", fontSize: 14 }} />
              <Text style={styles.errorText}>
                សូមបញ្ចូលពាក្យ "{getRequiredWord()}" ឲ្យបានត្រឹមត្រូវ
              </Text>
            </div>
          )}

          {isConfirmed && (
            <div style={styles.successFeedback}>
              <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 14 }} />
              <Text style={styles.successText}>
                បានបញ្ជាក់។ អ្នកអាច{lockStatus ? "ដោះសោ" : "ចាក់សោ"}បាន។
              </Text>
            </div>
          )}

          {/* Progress Indicator */}
          {inputConfirm && !isConfirmed && (
            <Progress
              percent={Math.min((inputConfirm.length / getRequiredWord().length) * 100, 100)}
              showInfo={false}
              strokeColor={lockStatus ? "#10b981" : "#ef4444"}
              trailColor="#f0f0f0"
              size="small"
              style={styles.progress}
            />
          )}
        </div>

        {/* Action Buttons */}
        <div style={styles.actionButtons}>
          <Button
            size="large"
            onClick={onCancel}
            disabled={loading}
            style={styles.cancelButton}
          >
            បោះបង់
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={handleConfirm}
            disabled={!isConfirmed || loading}
            loading={loading}
            style={{
              ...styles.confirmButton,
              ...(isConfirmed && !loading ? {
                backgroundColor: getActionColor(),
                borderColor: getActionColor(),
                boxShadow: `0 4px 12px ${getActionColor()}4D`,
              } : {}),
            }}
          >
            {loading 
              ? (lockStatus ? "កំពុងដោះសោ..." : "កំពុងចាក់សោ...") 
              : (lockStatus ? "ដោះសោ" : "ចាក់សោ")
            }
          </Button>
        </div>

        {/* Footer Note */}
        <div style={styles.footerNote}>
          <Text type="secondary" style={styles.noteText}>
            <MdOutlineWarning style={{ fontSize: 12, marginRight: 4 }} />
            {lockStatus 
              ? "បុគ្គលិកនឹងអាចចូលប្រើប្រាស់ប្រព័ន្ធវិញបន្ទាប់ពីដោះសោ"
              : "បុគ្គលិកនឹងមិនអាចចូលប្រើប្រាស់ប្រព័ន្ធបានទេបន្ទាប់ពីចាក់សោ"
            }
          </Text>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        
        .lock-user-modal .ant-modal-content {
          border-radius: 20px;
          overflow: hidden;
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .lock-user-modal .ant-modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #f0f0f0;
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .lock-user-modal .ant-modal-body {
          padding: 24px;
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .lock-user-modal .ant-modal-close {
          top: 20px;
          right: 20px;
        }
        
        .lock-user-modal .ant-btn {
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .lock-user-modal .ant-alert {
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .lock-user-modal .ant-tag {
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .lock-user-modal .ant-input {
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .lock-user-modal .ant-progress-text {
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .lock-user-modal .ant-typography {
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        
        .ant-alert-message {
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif !important;
        }
        
        .ant-alert-description {
          font-family: 'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif !important;
        }
      `}</style>
    </Modal>
  );
};

const styles = {
  modal: {
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  modalTitle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  iconWrapper: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  actionIcon: {
    fontSize: "18px",
  },
  titleText: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#1F2937",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  content: {
    padding: "8px 0",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  alert: {
    marginBottom: "20px",
    borderRadius: "12px",
  },
  staffCard: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "16px",
    backgroundColor: "#F9FAFB",
    borderRadius: "16px",
    marginBottom: "20px",
    border: "1px solid #E5E7EB",
  },
  staffAvatar: {
    flexShrink: 0,
  },
  avatar: {
    border: "2px solid white",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  staffInfo: {
    flex: 1,
  },
  staffLabel: {
    fontSize: "11px",
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  staffName: {
    margin: "4px 0 0 0",
    fontSize: "18px",
    fontWeight: 700,
    color: "#1F2937",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  staffPosition: {
    fontSize: "13px",
    color: "#6B7280",
    display: "block",
    marginTop: "2px",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  staffId: {
    marginTop: "6px",
    fontSize: "11px",
    borderRadius: "6px",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  statusSection: {
    marginBottom: "20px",
    padding: "12px",
    backgroundColor: "#F3F4F6",
    borderRadius: "12px",
    textAlign: "center",
  },
  statusBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  statusLabel: {
    fontSize: "13px",
    color: "#6B7280",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  statusTag: {
    padding: "4px 12px",
    borderRadius: "20px",
    fontWeight: 500,
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  confirmationSection: {
    marginBottom: "24px",
  },
  confirmationHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
  },
  warningIcon: {
    fontSize: "18px",
    color: "#faad14",
  },
  confirmationText: {
    fontSize: "13px",
    color: "#92400E",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  confirmInput: {
    height: "48px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 500,
    letterSpacing: "0.5px",
    transition: "all 0.3s ease",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  inputSuccess: {
    borderColor: "#52c41a",
    backgroundColor: "#F6FFED",
  },
  inputShake: {
    animation: "shake 0.5s ease-in-out",
  },
  errorFeedback: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "8px",
    padding: "6px 12px",
    backgroundColor: "#FEF2F2",
    borderRadius: "8px",
  },
  errorText: {
    fontSize: "12px",
    color: "#ff4d4f",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  successFeedback: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "8px",
    padding: "6px 12px",
    backgroundColor: "#F6FFED",
    borderRadius: "8px",
  },
  successText: {
    fontSize: "12px",
    color: "#52c41a",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  progress: {
    marginTop: "12px",
  },
  actionButtons: {
    display: "flex",
    gap: "12px",
    marginBottom: "16px",
  },
  cancelButton: {
    flex: 1,
    height: "44px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 500,
    borderColor: "#E5E7EB",
    color: "#6B7280",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  confirmButton: {
    flex: 1,
    height: "44px",
    borderRadius: "12px",
    fontSize: "14px",
    fontWeight: 600,
    backgroundColor: "#E5E7EB",
    borderColor: "#E5E7EB",
    color: "white",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
  footerNote: {
    textAlign: "center",
    paddingTop: "12px",
    borderTop: "1px solid #F3F4F6",
  },
  noteText: {
    fontSize: "11px",
    color: "#9CA3AF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    fontFamily: "'Content', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  },
};

export default LockUserModal;